import { Component, NgZone, OnInit, TemplateRef, ViewChild, OnDestroy } from '@angular/core';
import { TableConfigs } from 'src/app/common/components/data-table/data-table.component';
import { CampaignStatus } from 'src/app/data/constants/campaign-status.const';
import { createReactTemplateElement } from '@angular-react/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CellType, ICallToActionProps, IFetchSubRowDataParams, IStepProps, IStepsProps } from '@amobee/component-library';
import { environment } from 'src/environments/environment';
import { ListResponse } from 'src/app/data/interfaces/ListResponse';
import { TimezonePipe } from 'src/app/common/pipes/timezone.pipe';
import { ModalService } from 'src/app/services/modal.service';
import { CampaignAdapter } from 'src/adapters/campaign-adapter';
import { AutoCompleteMappedItem } from 'src/app/common/components/auto-complete/auto-complete.component';
import { addRemoveInvisibleScroll, applySearchAndPagination, doFilter } from 'src/app/misc/util/util';
import { BreadcrumbService } from 'src/app/services/breadcrumb.service';

@Component({
  selector: 'brand-campaign-list',
  templateUrl: './brand-campaign-list.component.html',
  styleUrls: ['./brand-campaign-list.component.scss']
})
export class BrandCampaignListComponent implements OnInit, OnDestroy {
  @ViewChild('statusTemplate', { static: true }) public statusTemplate: TemplateRef<any>;
  @ViewChild('statusPopover', { static: false }) public statusPopover: TemplateRef<any>;
  showReport: boolean = false;
  campaignDetail: any;
  campaignStatus = CampaignStatus;
  // if we already fetch response, save it to local variable so that we don't need to fetch again
  campaignListStepperMap = {};
  showContentLoading: boolean = false;
  refreshDelay: number = 500;
  customerId: number;
  advertiserId: number;
  advertiserName: string;
  advertiserList: AutoCompleteMappedItem[] = [];
  campaignSubRowsFilterOrderSettings = { limit: 25, columnKey: 'name', sortOrder: 'desc' };
  isDataAlreadyFetched: boolean = false;
  fullDataList: Array<any> = [];

  constructor(
    public ngZone: NgZone,
    private router: Router,
    private route: ActivatedRoute,
    private campaignAdapter: CampaignAdapter,
    private timezonePipe: TimezonePipe,
    private modal: ModalService,
    private breadcrumbService: BreadcrumbService
  ) { }

  ngOnInit() {
    addRemoveInvisibleScroll(true);
    if (this.route.snapshot.data.cbaId) {
      this.customerId = this.route.snapshot.data.cbaId.customerId;
      this.advertiserId = this.route.snapshot.data.cbaId.advertiserId;
      this.campaignAdapter.getAdvertiserIds(this.customerId).then((value: any[]) => {
        if (value) {
          this.advertiserList = value.map(v => {
            if (v.Id === this.advertiserId) {
              this.advertiserName = v.Name;
            }
            return {
              label: v.Name,
              value: v.Id
            }
          });
          // add all advertiser option
          this.advertiserList.unshift({
            label: 'All Advertisers',
            value: -1
          })
        }
      });

      this.breadcrumbService.prepareBreadcrumb(this.customerId);
    }
  }

  public searchNotFoundCallToAction: ICallToActionProps = {
    type: 'EmptySearchResults',
    title: 'No Results Found'
  };

  public reloadTableTriggered: any;
  public tableConfigs: TableConfigs = {
    disableSorting: false,
    defaultSort: {
      id: 'name',
      desc: true
    },
    uniqueIdAccessor: 'id',
    pageSize: 25, // default to 25
    isSelectionEnabled: false,
    isPaginationEnabled: true,
    showSummaryRow: false,
    stickyHeader: true,
    stickyColumnHeaders: true,
    enableStickyColumns: true,
    fetchStaticData: (params: any) => {
      this.showContentLoading = true;
      if (this.isDataAlreadyFetched) {
        this.showContentLoading = false;
        return Promise.resolve(doFilter(this.fullDataList, params));
      } else {
        let allCountPromises = [];
        let brandCampaignCountMap = {};
        return this.campaignAdapter.getBrandIds(this.customerId, this.advertiserId).then((value: any[]) => {
          value.forEach(v => {
            allCountPromises.push(this.campaignAdapter.getCampaignsCount(this.advertiserId, v.Id).then(count => {
              brandCampaignCountMap[v.Id] = count;
              return count;
            }));
          });
          return Promise.all(allCountPromises).then(() => {
            this.fullDataList = value.map(v => {
              return {
                id: v.Id,
                name: v.Name,
                hasSubRows: !!brandCampaignCountMap[v.Id],
                isParentRow: true
              }
            });
            this.showContentLoading = false;
            this.isDataAlreadyFetched = true;
            return doFilter(this.fullDataList, params);
          });
        });
      }
    },
    showCallToAction: true,
    callToActionProps: this.searchNotFoundCallToAction,
    isRowExpandEnabled: true,
    fetchSubRows: (fetchSubRowParams: IFetchSubRowDataParams) => {
      if (fetchSubRowParams.fetchParams.sortBy && fetchSubRowParams.fetchParams.sortBy.length) {
        this.campaignSubRowsFilterOrderSettings.columnKey = fetchSubRowParams.fetchParams.sortBy[0].id;
        this.campaignSubRowsFilterOrderSettings.sortOrder = fetchSubRowParams.fetchParams.sortBy[0].desc ? 'desc' : 'asc';
      }
      return this.campaignAdapter.getCampaignListData(this.campaignSubRowsFilterOrderSettings, { brandId: fetchSubRowParams.row.original.id }).then((value: ListResponse) => {
        value.results.forEach(row => {
          row.brandId = fetchSubRowParams.row.original.id;
        });
        return value.results;
      });
    }
  };
  public columns: any[] = [
    {
      Header: 'Brand / Campaign',
      accessor: 'name',
      id: 'name',
      isRowActionColumn: true,
      sticky: 'left',
      cellType: CellType.LINK,
      onLinkClick: row => {
        this.showContentLoading = true;
        if (row.original.isParentRow) {
          // brand clicked
          this.campaignAdapter.updateCBAId(this.customerId, this.advertiserId, row.original.id);
          this.router.navigate(['campaign/list']).catch((e) => {
            console.error(e);
            this.showContentLoading = false;
          });
        } else {
          // campaign clicked
          this.campaignAdapter.updateCBAId(this.customerId, this.advertiserId, row.original.brandId);
          if (row.original.status === CampaignStatus.Draft.key) {
            this.router.navigate(['campaign/edit', row.original.id]).catch((e) => {
              console.error(e);
              this.showContentLoading = false;
            });
          } else {
            this.router.navigate(['campaign/summary', row.original.id]).catch((e) => {
              console.error(e);
              this.showContentLoading = false;
            });
          }
        }
      },
      isActive: () => true,
      rowActionButtons: [
        {
          buttonType: 'button',
          iconName: 'Edit',
          onClick: row => {
            this.showContentLoading = true;
            this.router.navigate(['campaign/edit', row.original.id]).catch((e) => {
              console.error(e);
              this.showContentLoading = false;
            });
          },
          titleText: 'Edit',
          isHidden: row => {
            return row.original.isParentRow || !(row.original.status === CampaignStatus.Draft.key);
          }
        },
        {
          buttonType: 'button',
          iconName: 'Trash',
          onClick: row => {
            this.modal.confirmation({ message: `Are you sure you want to delete campaign ${row.original.name}?` }, '', '', () => {
              this.campaignAdapter.deleteCampaign(row.original.id).then(resp => {
                this.showContentLoading = true;
                setTimeout(() => {
                  this.isDataAlreadyFetched = false;
                  this.reloadTableTriggered = {};
                }, this.refreshDelay);
              });
            });
          },
          titleText: 'Delete',
          isHidden: row => {
            return row.original.isParentRow || !(row.original.status === CampaignStatus.Draft.key);
          }
        },
        {
          buttonType: 'button',
          iconName: 'View',
          onClick: row => {
            this.showContentLoading = true;
            this.router.navigate(['campaign/edit', row.original.id], { queryParams: { readOnly: true } }).catch((e) => {
              console.error(e);
              this.showContentLoading = false;
            });
          },
          titleText: 'View',
          isHidden: row => {
            return row.original.isParentRow || row.original.status === CampaignStatus.Draft.key;
          }
        },
        {
          buttonType: 'button',
          iconName: 'Report',
          onClick: row => {
            this.showReport = true;
            this.campaignDetail = row.original;
          },
          titleText: 'Report',
          isHidden: row => {
            return row.original.isParentRow || row.original.status === CampaignStatus.Draft.key;
          }
        }
      ]
    },
    {
      Header: 'Status',
      accessor: 'status',
      id: 'status',
      Cell: props => {
        return createReactTemplateElement(this.statusTemplate, props.row.original, this.ngZone);
      },
      disableSortBy: true
    },
    {
      Header: 'ID',
      accessor: 'id',
      id: 'id',
      width: 130,
      disableSortBy: true
    },
    {
      Header: 'Start Date',
      accessor: row => {
        return row.startDate ? `${this.timezonePipe.transform(row.startDate, 'L LT', environment.timezone)} ${environment.timezoneAbbr}` : '';
      },
      id: 'startDate',
      width: 200,
      disableSortBy: true
    },
    {
      Header: 'End Date',
      accessor: row => {
        return row.endDate ? `${this.timezonePipe.transform(row.endDate, 'L LT', environment.timezone)} ${environment.timezoneAbbr}` : '';
      },
      id: 'endDate',
      width: 200,
      disableSortBy: true
    },
    {
      Header: 'Budget',
      accessor: 'budget',
      id: 'budget',
      disableSortBy: true
    },
    {
      Header: 'Additional Bonus',
      accessor: 'bonus',
      id: 'bonus',
      disableSortBy: true
    }
  ];

  closeStatusSidebar() {
    this.showReport = false;
    this.campaignDetail = null;
  }

  fetchStepperProps(campaignId: number, status: string, budget: string) {
    if (status === CampaignStatus.Pending.key && !this.campaignListStepperMap[campaignId]) {
      this.campaignListStepperMap[campaignId] = {};
      let budgetValue = budget && budget.trim() ? parseFloat(budget.trim().replace(/[^\d.-]/g, '')) : 0;
      this.campaignAdapter
        .getCampaignStepperProps(undefined, campaignId, budgetValue)
        .then((v: { steps: Array<IStepProps>; completedStep: IStepsProps['completedStep'] }) => {
          this.campaignListStepperMap[campaignId] = v;
        });
    }
  }

  autoCompletetConfig = {
    advertiserAutoCompletePromise: (params: any) => {
      return Promise.resolve(applySearchAndPagination(this.advertiserList, params));
    },
    handleOnAdvertiserSelect: (item: AutoCompleteMappedItem) => {
      if (item.value !== -1) {
        this.campaignAdapter.updateCBAId(this.customerId, Number(item.value), null);
        this.advertiserId = Number(item.value);
        this.advertiserName = item.label;
        this.isDataAlreadyFetched = false;
        this.reloadTableTriggered = {};
        //this.router.navigate(['advertiser/campaign-list']);
      } else {
        // all advertiser
        this.router.navigate(['advertiser/brand-list']);
      }
    },
    responseMapper: (response: any, offset: number, limit: number) => {
      return {
        results: response,
        hasMore: response.length >= limit
      }
    }
  };

  ngOnDestroy(): void {
    addRemoveInvisibleScroll(false);
  }

}

